/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Which markers a surface shows and where. A surface is one location's floor plan, or
 * its placeholder box; when several locations share a floor plan the surface shows all
 * of their sensors and gateways.
 */

import {
  type ImageMeta,
  type InStoreAsset,
  type InStoreSensor,
  type MarkerRef,
  type Position,
  markerKey,
} from './types'

const round4 = (value: number): number => Math.round(value * 10000) / 10000

export interface SurfaceMarker {
  key: string
  ref: MarkerRef
  asset: InStoreAsset
  /** Present for a sensor marker; absent for the gateway. */
  sensor?: InStoreSensor
  position: Position
  /** False when the position is the default spread, not a saved or dragged one. */
  placed: boolean
}

/** The locations whose markers a surface shows: every one that shares the image, else just the one. */
export const surfaceAssets = (
  selected: InStoreAsset | undefined,
  assets: InStoreAsset[],
  imageFor: (assetId: number) => ImageMeta | undefined
): InStoreAsset[] => {
  if (!selected) return []
  const image = imageFor(selected.id)
  if (!image) return [selected]
  return assets.filter((asset) => imageFor(asset.id)?.id === image.id)
}

/**
 * The location whose surface the pane shows without waiting for a click: the only
 * listed location (with or without a floor plan), or, when the listed locations have
 * exactly one floor plan between them, the first that shows it. Locations without a
 * plan don't count against it. Undefined when there are none, or several plans.
 */
export const autoSurfaceLocation = (
  locations: InStoreAsset[],
  imageFor: (assetId: number) => ImageMeta | undefined
): number | undefined => {
  if (locations.length === 1) return locations[0].id
  const withPlan = locations.filter((asset) => imageFor(asset.id))
  const planIds = new Set(withPlan.map((asset) => imageFor(asset.id)!.id))
  return planIds.size === 1 ? withPlan[0].id : undefined
}

/**
 * Markers with a saved position (or a dragged one from the draft) sit where they were
 * put; the rest are spread on a grid inside the surface so nothing starts stacked in a
 * corner. The spread is a function of the unplaced set, so it is stable across renders.
 */
export const surfaceMarkers = (assets: InStoreAsset[], draft: Record<string, Position>): SurfaceMarker[] => {
  const markers: SurfaceMarker[] = []
  const unplaced: SurfaceMarker[] = []
  for (const asset of assets) {
    const refs: { ref: MarkerRef; sensor?: InStoreSensor; stored: Position | null }[] = [
      ...(asset.gateway ? [{ ref: { type: 'gateway' as const, id: asset.id }, stored: asset.gateway.position }] : []),
      ...asset.sensors.map((sensor) => ({
        ref: { type: 'sensor' as const, id: sensor.id },
        sensor,
        stored: sensor.position,
      })),
    ]
    for (const { ref, sensor, stored } of refs) {
      const key = markerKey(ref)
      const position = draft[key] ?? stored
      const marker: SurfaceMarker = {
        key,
        ref,
        asset,
        sensor,
        position: position ?? { x: 0, y: 0 },
        placed: !!position,
      }
      markers.push(marker)
      if (!position) unplaced.push(marker)
    }
  }
  const cols = Math.ceil(Math.sqrt(unplaced.length))
  const rows = cols === 0 ? 0 : Math.ceil(unplaced.length / cols)
  unplaced.forEach((marker, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    marker.position = { x: round4(0.1 + (0.8 * (col + 0.5)) / cols), y: round4(0.1 + (0.8 * (row + 0.5)) / rows) }
  })
  return markers
}

export const clampFraction = (value: number): number => round4(Math.min(1, Math.max(0, value)))

export const MIN_MARKER_PX = 24
export const MAX_MARKER_PX = 48
/** The placeholder box's shape when a location has no floor plan. */
export const PLACEHOLDER_ASPECT = 4 / 3

export interface Size {
  width: number
  height: number
}

/**
 * Marker size from the rendered surface width, so a layout arranged on a big monitor
 * still reads on a small one: about 24px on the ~600px surface an iPad leaves beside
 * the list, low 30s on a laptop, mid 40s on a desktop. Positions are marker centres,
 * so a size change never moves a marker off its spot.
 */
export const markerSizeFor = (surfaceWidth: number): number =>
  Math.min(MAX_MARKER_PX, Math.max(MIN_MARKER_PX, Math.round(surfaceWidth / 28)))

/**
 * The box an image of `aspect` (width / height) renders in: as large as fits the pane
 * in both directions, never larger than its native size. The whole plan is always in
 * view, so the surface never scrolls and a marker at (0.5, 0.5) is always mid-image.
 */
export const fitBox = (pane: Size, aspect: number, natural?: Size): Size => {
  let width = Math.min(pane.width, pane.height * aspect)
  if (natural) width = Math.min(width, natural.width)
  width = Math.max(0, Math.floor(width))
  return { width, height: Math.floor(width / aspect) }
}
